import {toast} from 'react-toastify';
import {twMerge} from 'tailwind-merge';
import {FC, ReactNode, useEffect, useState} from 'react';
import {deleteBrand, updateBrand, updateOrganization, VerdocsEndpoint} from '@verdocs/js-sdk';
import {ArrowTopRightOnSquareIcon, ShieldCheckIcon, Squares2X2Icon, XMarkIcon} from '@heroicons/react/24/outline';
import {BookOpenIcon, EllipsisVerticalIcon, EnvelopeIcon, GlobeAltIcon, PencilIcon} from '@heroicons/react/24/outline';
import {DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger} from '../../Components/DropdownMenu';
import {invalidateAllQueries, useBrand, useOrganization} from '../../react-sdk/queries';
import {DropdownMenu, DropdownMenuContent} from '../../Components/DropdownMenu';
import {BrandSections, type TBrandSection} from './BrandSections';
import {OkCancelDialog} from '../../Dialogs/OkCancelDialog';
import {brandInitials, brandTileColor} from './BrandThemes';
import {Spinner} from '../../Components';
import {AppState} from '../../AppState';

type TDevSection = 'sending' | 'signing' | 'usecases';
type TAdminSection = 'setup' | 'choosing' | 'domains';

// The reference panel behind every Overview tile. Developer entries carry code, admin entries
// carry steps; both share the video slot and doc links. A link without an href has no page yet.
interface IGuidePanel {
  kind: 'dev' | 'admin';
  title: string;
  video: string;
  links: {label: string; href?: string}[];
  codeTitle?: string;
  snippets?: {id: string; label: string; code: string}[];
  stepsTitle?: string;
  steps?: string[];
}

// One centered column for the title bar's contents, the strip, and everything beneath, with
// gutters that step down on smaller screens so wide monitors do not stretch the page.
const COLUMN = 'mx-auto w-full max-w-[1080px] px-2 tablet:px-6 laptop:px-10';

/** The strip's five stops. */
export type THubTab = 'overview' | 'communications' | 'hosting' | 'signing' | 'certification';

type TStopState = 'off' | 'on' | 'warn';

interface BrandHubProps {
  brandId: string;
  onBack: () => void;
  onOpenBuilder: () => void;
  onOpenEmailBuilder: () => void;
  onOpenDisclosures: () => void;
  onOpenPreview: () => void;
  // The tab lives in the URL, so the parent owns it.
  tab: THubTab;
  onTabChange: (tab: THubTab) => void;
  // A builder can hand the user back here with a developer panel to open (the Email Builder's
  // bring-your-own-HTML link), since the panels live on the Overview.
  openDevPanel?: string | null;
  onDevPanelHandled?: () => void;
}

const STOPS: {id: THubTab; label: string; icon: ReactNode}[] = [
  {id: 'overview', label: 'Overview', icon: <Squares2X2Icon className="size-[22px]" />},
  {id: 'communications', label: 'Communications', icon: <EnvelopeIcon className="size-[22px]" />},
  {id: 'hosting', label: 'Hosting', icon: <GlobeAltIcon className="size-[22px]" />},
  {id: 'signing', label: 'Signing', icon: <PencilIcon className="size-[22px]" />},
  {id: 'certification', label: 'Certification', icon: <ShieldCheckIcon className="size-[22px]" />},
];

// The node carries one fact, whether the stop is set up (green stroke) or needs attention
// (amber). Selection lives on the label and the bar under it, never on the node.
const Stop: FC<{label: string; icon: ReactNode; state: TStopState; overview?: boolean; selected: boolean; onClick: () => void}> = ({
  label,
  icon,
  state,
  overview = false,
  selected,
  onClick,
}) => (
  <button
    onClick={onClick}
    aria-current={selected ? 'page' : undefined}
    className="group relative z-[1] flex min-w-0 max-w-28 flex-1 flex-col items-center text-center focus:outline-none">
    <span
      className={twMerge(
        'flex h-[54px] w-[54px] items-center justify-center rounded-[14px] border-[1.5px] border-gray-300 bg-white text-gray-600 transition-all',
        'group-hover:-translate-y-0.5 group-hover:shadow-md group-hover:shadow-indigo-100 group-focus-visible:ring-2 group-focus-visible:ring-indigo-300',
        overview && 'bg-gray-50',
        state === 'on' && 'border-2 border-[#50bd80] text-[#2d7648] [&_svg]:stroke-[2.4]',
        state === 'warn' && 'border-2 border-amber-500 text-amber-800 [&_svg]:stroke-[2.4]',
      )}>
      {icon}
    </span>
    <span
      className={twMerge(
        'mt-2 text-[13.5px] font-medium text-gray-800 transition-colors group-hover:text-indigo-600',
        selected && 'font-semibold text-indigo-600',
      )}>
      {label}
    </span>
    <span
      className={twMerge(
        'mt-1.5 h-[3px] w-7 rounded-full transition-colors',
        selected ? 'bg-indigo-600' : 'bg-transparent group-hover:bg-indigo-200',
      )}
    />
  </button>
);

// The tile the For Developers section shipped with; For Admins reuses it with an icon in
// place of the code glyph.
const HubTile: FC<{glyph: ReactNode; title: string; blurb: string; action: string; onClick: () => void}> = ({
  glyph,
  title,
  blurb,
  action,
  onClick,
}) => (
  <button
    onClick={onClick}
    className="flex flex-col items-start rounded-2xl border border-dashed border-gray-300 bg-gray-50/60 px-5 py-4 text-left transition-all hover:-translate-y-px hover:border-solid hover:border-indigo-400 hover:bg-indigo-50/30 hover:shadow-lg hover:shadow-indigo-100">
    <span className="mb-2.5 flex h-5 items-center text-indigo-400">{glyph}</span>
    <b className="mb-1.5 block text-sm font-semibold text-gray-800">{title}</b>
    <span className="block text-xs leading-relaxed text-gray-500">{blurb}</span>
    <em className="mt-2.5 block text-xs font-medium not-italic text-indigo-600">{action} &rarr;</em>
  </button>
);

const ADMIN_TILES: {id: TAdminSection; title: string; blurb: string}[] = [
  {
    id: 'setup',
    title: 'Getting Set Up',
    blurb: 'What each tab controls and the order that gets a new brand ready for its first envelope.',
  },
  {
    id: 'choosing',
    title: 'Choosing a Brand When Sending',
    blurb: 'The organization default, picking a brand per envelope, and how child organizations inherit.',
  },
  {
    id: 'domains',
    title: 'Domains and Deliverability',
    blurb: 'Sending from your own address, serving signing links on your subdomain, and what happens while DNS propagates.',
  },
];

const DOCS = 'https://developers.verdocs.com/docs';
const EMAIL_DOMAIN_GUIDE = `${DOCS}/guides/tutorials/add-a-custom-email-domain-to-your-brand`;
const HIERARCHIES_GUIDE = `${DOCS}/guides/tutorials/organization-hierarchies`;
const CREATE_ENVELOPE_REF = `${DOCS}/reference/Rest-API/api-docs/envelopes/createEnvelope`;
const EMBEDDED_SIGNING_GUIDE = `${DOCS}/guides/tutorials/embedded-signing-in-react`;

// Video slots are not wired yet.
const adminSections = (brandName: string): Record<TAdminSection, IGuidePanel> => ({
  setup: {
    kind: 'admin',
    title: 'Getting Set Up',
    video: 'Setting up a brand',
    links: [
      {label: 'What each tab controls'},
      {label: 'Logos and colors that work in email and on screen'},
      {label: 'Publishing from the Theme Editor'},
      {label: 'Sending a test envelope'},
    ],
    stepsTitle: 'In order',
    steps: [
      'Signing: pick a theme or match your web site, then publish. This paints the signing page and seeds the colors the email uses.',
      'Communications: open the Email Builder to check the invitation, then set up a custom sender if mail should come from your own domain.',
      'Hosting: point a subdomain at us so signing links open on your domain instead of app.verdocs.com.',
      'Certification: set the certified-by and location text that PDF readers show on the signature.',
    ],
  },
  choosing: {
    kind: 'admin',
    title: 'Choosing a Brand When Sending',
    video: 'Brands in the send dialog',
    links: [
      {label: 'How notifications choose a brand', href: `${EMAIL_DOMAIN_GUIDE}#how-notifications-choose-a-brand`},
      {label: 'Choosing a brand from the API', href: CREATE_ENVELOPE_REF},
      {label: 'Organization hierarchies', href: HIERARCHIES_GUIDE},
      {label: 'Listing brands and their keys', href: `${DOCS}/reference/Rest-API/api-docs/brands/getBrands`},
    ],
    stepsTitle: 'How a brand is chosen',
    steps: [
      `Make one brand the organization default and every envelope sent without a choice uses it. ${brandName} is set from the menu at the top right.`,
      'In the send dialog, the Brand row lets a sender pick a different brand for that one envelope.',
      "Child organizations use their parent's default until they set a default of their own.",
      'Integrations pass a brand key with each envelope; the documentation links cover it.',
    ],
  },
  domains: {
    kind: 'admin',
    title: 'Domains and Deliverability',
    video: 'Custom sender and signing domains',
    links: [
      {label: 'Setting up a custom email domain', href: EMAIL_DOMAIN_GUIDE},
      {label: 'Email domain status, from pending to verified', href: `${EMAIL_DOMAIN_GUIDE}#status-lifecycle`},
      {
        label: "When recipients don't get the email",
        href: `${DOCS}/guides/tutorials/why-am-i-not-receiving-verdocs-email-notifications#solution-4-notification-settings-and-how-your-sender-delivers-mail`,
      },
      {label: 'Custom signing domains'},
    ],
    stepsTitle: 'What each domain does',
    steps: [
      'A custom sender sends mail from your own address instead of notifications@verdocs.com. Add its DNS records under Communications and verify them.',
      'A custom signing domain serves signing links from your subdomain. Add the CNAME record under Hosting and we issue the certificate.',
      'Until a domain verifies, the brand keeps using the Verdocs address and domain, so nothing changes for recipients while DNS propagates.',
      'Both are set per brand, so different brands can send from and sign on different domains.',
    ],
  },
});

const DEV_TILES: {id: TDevSection; title: string; blurb: string}[] = [
  {
    id: 'sending',
    title: 'Sending and Delivery',
    blurb: 'Create envelopes under a brand, control the from address, and track delivery events.',
  },
  {id: 'signing', title: 'Signing and Embedding', blurb: 'Embed the signer in your own app and apply the same published styles.'},
  {
    id: 'usecases',
    title: 'Use-Cases and Solutions',
    blurb: 'Tutorials for common workflows, and runnable quickstarts for every framework.',
  },
];

// Video slots are not wired yet.
const devSections = (brandKey: string): Record<TDevSection, IGuidePanel> => ({
  sending: {
    kind: 'dev',
    title: 'Sending and Delivery',
    video: 'Branding what you send',
    links: [
      {label: 'Brands API reference', href: `${DOCS}/reference/Rest-API/api-docs/brands/getBrands`},
      {label: 'Creating envelopes', href: CREATE_ENVELOPE_REF},
      {label: 'Sending your own notifications', href: `${DOCS}/guides/tutorials/bring-your-own-notification`},
      {label: 'Webhook events', href: `${DOCS}/reference/Rest-API/webhooks`},
    ],
    codeTitle: 'Send with this brand',
    snippets: [
      {
        id: 'node',
        label: 'Node',
        code: `import {VerdocsEndpoint, createEnvelope} from '@verdocs/js-sdk';

// Authenticated with an API key (client_credentials).
const endpoint = VerdocsEndpoint.getDefault();

await createEnvelope(endpoint, {
  template_id: 'tpl_8f3a91c4',
  brand_key: '${brandKey}',   // omit to use the org default
  recipients: [
    {role_name: 'Recipient 1', first_name: 'Jane', last_name: 'Doe', email: 'jane@example.com'},
  ],
});`,
      },
      {
        id: 'react',
        label: 'React',
        code: `import {VerdocsSend} from '@verdocs/web-sdk-react';

// The send flow inherits the brand you pass here.
<VerdocsSend
  templateId={templateId}
  brandKey="${brandKey}"
  onSend={(e) => console.log(e.detail)}
/>`,
      },
      {
        id: 'curl',
        label: 'cURL',
        code: `curl -X POST https://api.verdocs.com/v2/envelopes \\
  -H "Authorization: Bearer $VERDOCS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "template_id": "tpl_8f3a91c4",
    "brand_key": "${brandKey}",
    "recipients": [
      {"role_name": "Recipient 1", "first_name": "Jane", "last_name": "Doe", "email": "jane@example.com"}
    ]
  }'`,
      },
    ],
  },
  signing: {
    kind: 'dev',
    title: 'Signing and Embedding',
    video: 'Embedding a branded signer',
    links: [
      {label: 'Embedded signing in React', href: EMBEDDED_SIGNING_GUIDE},
      {label: 'Branding an embedded signer', href: `${EMBEDDED_SIGNING_GUIDE}#branding`},
      {label: 'Styling Web SDK components', href: `${DOCS}/reference/SDK/web/styling`},
      {label: 'Verifying a signed PDF', href: `${DOCS}/guides/tutorials/verifying-a-completed-verdocs-pdf-signature`},
      {label: 'Custom signing domains'},
    ],
    codeTitle: 'Embed and style the signer',
    snippets: [
      {
        id: 'react',
        label: 'React',
        code: `import {VerdocsSign} from '@verdocs/web-sdk-react';

// Brand styles load automatically from the envelope's brand.
<VerdocsSign
  envelopeId={envelopeId}
  roleId="Recipient 1"
  inviteCode={inviteCode}
/>`,
      },
      {
        id: 'css',
        label: 'CSS overrides',
        code: `/* The variables the Theme Editor publishes. Set them in your
   own app to match the hosted signing experience. */
:root {
  --verdocs-primary-color: #654dcb;
  --verdocs-button-radius: 999px;
  --verdocs-field-background: #8DC63F1A;
}`,
      },
    ],
  },
  usecases: {
    kind: 'dev',
    title: 'Use-Cases and Solutions',
    video: 'Patterns and sample apps',
    links: [
      {label: 'Server workflow with static templates', href: `${DOCS}/guides/tutorials/server-workflow-with-static-templates`},
      {label: 'Sending an envelope without a template', href: `${DOCS}/guides/tutorials/send-an-envelope-without-a-template`},
      {label: 'Tagging dynamic documents', href: `${DOCS}/guides/tutorials/tagging-dynamic-documents`},
      {label: 'IFRAME embeds', href: `${DOCS}/guides/tutorials/iframe-embeds`},
      {label: 'Organization hierarchies', href: HIERARCHIES_GUIDE},
    ],
    codeTitle: 'Start from a quickstart',
    snippets: [
      {
        id: 'clone',
        label: 'Clone',
        code: `# Runnable quickstarts for React, Vue, Angular, Next.js, web
# components, Node, Python, and C#, under apps/.
git clone https://github.com/Verdocs/hub
cd hub

pnpm install
pnpm --filter verdocs-quickstart-react start`,
      },
    ],
  },
});

export const BrandHub: FC<BrandHubProps> = ({
  brandId,
  onBack,
  onOpenBuilder,
  onOpenEmailBuilder,
  onOpenDisclosures,
  onOpenPreview,
  tab,
  onTabChange: setTab,
  openDevPanel = null,
  onDevPanelHandled,
}) => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const brandQuery = useBrand(organizationId, brandId);
  const orgQuery = useOrganization(organizationId);
  const [devPanel, setDevPanel] = useState<TDevSection | null>(null);
  const [adminPanel, setAdminPanel] = useState<TAdminSection | null>(null);
  const [devSnip, setDevSnip] = useState(0);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // The brand name while it is being edited in place; null when the title shows as text.
  const [renaming, setRenaming] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const brand = brandQuery.data;
  const isDefault = !!brand && orgQuery.data?.default_brand_id === brand.id;

  const closePanel = () => {
    setDevPanel(null);
    setAdminPanel(null);
  };

  useEffect(() => {
    if (!devPanel && !adminPanel) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closePanel();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [devPanel, adminPanel]);

  useEffect(() => {
    if (!openDevPanel) return;
    setDevSnip(0);
    setDevPanel(openDevPanel as TDevSection);
    onDevPanelHandled?.();
  }, [openDevPanel, onDevPanelHandled]);

  if (!brand) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner mode="dark" />
      </div>
    );
  }

  const logoUrl = brand.thumbnail_url || brand.full_logo_url;

  const stopState: Record<Exclude<THubTab, 'overview'>, TStopState> = {
    communications:
      brand.email_domain && brand.email_domain_status !== 'verified'
        ? 'warn'
        : brand.primary_color || brand.email_domain_status === 'verified'
          ? 'on'
          : 'off',
    hosting: !brand.app_domain ? 'off' : brand.app_domain_status === 'active' ? 'on' : 'warn',
    signing: brand.style_overrides || brand.primary_color ? 'on' : 'off',
    certification: brand.pdf_signature_reason || brand.pdf_signature_location ? 'on' : 'off',
  };

  const handleToggleDefault = async () => {
    const newDefault = isDefault ? null : brand.id;
    setWorking(true);
    try {
      await updateOrganization(VerdocsEndpoint.getDefault(), organizationId, {default_brand_id: newDefault} as any);
      invalidateAllQueries();
      toast.success(newDefault ? `${brand.name || brand.key} set as default` : 'Default brand cleared');
    } catch (e: any) {
      toast.error(`Error updating default brand: ${e.message}`);
    }
    setWorking(false);
  };

  const handleRename = async () => {
    if (renaming === null) return;
    const name = renaming.trim();
    setRenaming(null);
    if (name === (brand.name || '')) return;
    try {
      await updateBrand(VerdocsEndpoint.getDefault(), organizationId, brand.id, {name: name || null});
      invalidateAllQueries();
      toast.success('Brand renamed');
    } catch (e: any) {
      toast.error(`Error renaming brand: ${e.response?.data?.error || e.message}`);
    }
  };

  const handleDelete = async () => {
    setWorking(true);
    try {
      await deleteBrand(VerdocsEndpoint.getDefault(), organizationId, brand.id);
      invalidateAllQueries();
      toast.success('Brand deleted');
      onBack();
    } catch (e: any) {
      toast.error(`Error deleting brand: ${e.response?.data?.error || e.message}`);
    }
    setConfirmingDelete(false);
    setWorking(false);
  };

  const copySnippet = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Copied');
  };

  const section: IGuidePanel | null = devPanel
    ? devSections(brand.key)[devPanel]
    : adminPanel
      ? adminSections(brand.name || brand.key)[adminPanel]
      : null;
  const snippets = section?.snippets || [];
  const activeSnippet = snippets[Math.min(devSnip, Math.max(snippets.length - 1, 0))];

  return (
    <div className="flex flex-1 flex-col box-border overflow-y-scroll">
      {/* Title bar */}
      <div className="border-b border-gray-200 pb-3.5">
        <div className={twMerge(COLUMN, 'flex items-center gap-3')}>
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-10 w-10 rounded-lg bg-gray-50 object-contain" />
          ) : (
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg text-[14px] font-extrabold text-white"
              style={{background: brandTileColor(brand)}}>
              {brandInitials(brand.name, brand.key)}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {renaming !== null ? (
                <input
                  autoFocus
                  value={renaming}
                  maxLength={100}
                  onChange={(e) => setRenaming(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onBlur={handleRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                  aria-label="Brand name"
                  className="w-72 max-w-full rounded-md border border-indigo-300 px-1.5 py-0 text-[17px] font-semibold text-gray-900 outline-none ring-2 ring-indigo-100"
                />
              ) : (
                <>
                  <h1 className="truncate text-[17px] font-semibold text-gray-900">{brand.name || brand.key}</h1>
                  <button
                    onClick={() => setRenaming(brand.name || '')}
                    className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-indigo-600"
                    title="Rename brand">
                    <PencilIcon className="size-3.5" />
                  </button>
                </>
              )}
              {isDefault && (
                <span className="whitespace-nowrap rounded-full bg-indigo-50 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-indigo-600">
                  Default
                </span>
              )}
            </div>
            <span className="block font-mono text-[11px] text-gray-400">{brand.key}</span>
          </div>
          <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50" title="Brand actions">
                <EllipsisVerticalIcon className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem className="cursor-pointer" onClick={onOpenBuilder}>
                Open Theme Editor
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={onOpenEmailBuilder}>
                Open Email Builder
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={onOpenPreview}>
                Full preview
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={handleToggleDefault}>
                {isDefault ? 'Clear org default' : 'Set as org default'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer text-red-600 focus:text-red-600" onClick={() => setConfirmingDelete(true)}>
                Delete brand
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            onClick={onBack}
            className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50"
            title="Back to Brands">
            <XMarkIcon className="size-4" />
          </button>
        </div>
      </div>

      <div className={COLUMN}>
        {/* Tab strip */}
        {/* Each stop is at most 7rem wide and the outer two sit on the column's edges, so the rail
            runs center to center: 3.5rem in, or 10% once the column is too narrow for 7rem stops. */}
        <div className="relative mt-5 flex justify-between pb-3">
          <div className="absolute left-[min(10%,3.5rem)] right-[min(10%,3.5rem)] top-[27px] h-[1.5px] bg-gray-300" />
          {STOPS.map((stop) => (
            <Stop
              key={stop.id}
              label={stop.label}
              icon={stop.icon}
              overview={stop.id === 'overview'}
              state={stop.id === 'overview' ? 'off' : stopState[stop.id]}
              selected={tab === stop.id}
              onClick={() => setTab(stop.id)}
            />
          ))}
        </div>

        {tab === 'overview' && (
          <>
            <div className="mt-7 text-sm leading-relaxed text-gray-600">
              <p className="mb-2.5">
                A brand is the set of logos, colors, names, and links Verdocs uses wherever a recipient meets you: the invitation email, the
                page they sign on, and the signed PDF they keep. Each tab above covers one of those places and holds every setting for it.
              </p>
              <p>
                Your organization has one default brand, used for every envelope sent without a brand chosen. Add more brands for business
                lines, clients, or partners, and pick one per envelope in the send dialog or by key from the API. Child organizations
                inherit this one unless they set their own.
              </p>
            </div>

            <h2 className="mt-9 text-sm font-semibold uppercase tracking-wide text-gray-700">For admins</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 tablet:grid-cols-3">
              {ADMIN_TILES.map((tile) => (
                <HubTile
                  key={tile.id}
                  glyph={<BookOpenIcon className="size-[18px]" />}
                  title={tile.title}
                  blurb={tile.blurb}
                  action="Open guide"
                  onClick={() => setAdminPanel(tile.id)}
                />
              ))}
            </div>

            <h2 className="mt-12 text-sm font-semibold uppercase tracking-wide text-gray-700">For developers</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 tablet:grid-cols-3">
              {DEV_TILES.map((tile) => (
                <HubTile
                  key={tile.id}
                  glyph={<span className="font-mono text-sm">&lt;/&gt;</span>}
                  title={tile.title}
                  blurb={tile.blurb}
                  action="Open reference"
                  onClick={() => {
                    setDevSnip(0);
                    setDevPanel(tile.id);
                  }}
                />
              ))}
            </div>
          </>
        )}

        {tab !== 'overview' && (
          <div className="mt-6">
            <BrandSections
              brandId={brandId}
              section={tab as TBrandSection}
              onOpenBuilder={onOpenBuilder}
              onOpenEmailBuilder={onOpenEmailBuilder}
              onOpenDisclosures={onOpenDisclosures}
            />
          </div>
        )}
      </div>

      {section && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/45 p-6"
          onClick={(e) => e.target === e.currentTarget && closePanel()}>
          <div className="flex max-h-[88vh] w-[780px] max-w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
              {section.kind === 'dev' ? (
                <span className="font-mono text-[15px] text-indigo-400">&lt;/&gt;</span>
              ) : (
                <BookOpenIcon className="size-5 text-indigo-400" />
              )}
              <h3 className="flex-1 text-[15.5px] font-semibold text-gray-900">
                {section.kind === 'dev' ? 'For developers' : 'For admins'}
                <small className="mt-0.5 block text-xs font-normal text-gray-400">{section.title}</small>
              </h3>
              <button
                onClick={closePanel}
                className="rounded-md px-2 py-0.5 text-xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                &times;
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-5">
              <div className="mb-5 grid grid-cols-1 gap-5 tablet:grid-cols-[1.1fr_1fr]">
                <div className="flex aspect-video flex-col items-center justify-center gap-2 rounded-xl border border-gray-100 bg-gray-50">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7-11-7Z" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-gray-500">{section.video}</span>
                  <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-500">
                    Coming soon
                  </span>
                </div>
                <div>
                  <h4 className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Documentation</h4>
                  <ul>
                    {section.links.map((link) => (
                      <li key={link.label} className="border-b border-gray-50 last:border-b-0">
                        {link.href ? (
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center justify-between gap-2.5 py-2 text-xs text-indigo-600 no-underline hover:text-indigo-800">
                            <span className="group-hover:underline">{link.label}</span>
                            <ArrowTopRightOnSquareIcon className="size-3.5 shrink-0 text-indigo-300 group-hover:text-indigo-600" />
                          </a>
                        ) : (
                          <span className="flex items-center justify-between gap-2.5 py-2 text-xs text-gray-600">
                            {link.label}
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                              Soon
                            </span>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {section.steps && (
                <>
                  <h4 className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{section.stepsTitle}</h4>
                  <ol className="flex flex-col gap-2.5">
                    {section.steps.map((step, i) => (
                      <li key={step} className="flex gap-3 text-xs leading-relaxed text-gray-600">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-[10px] font-semibold text-indigo-600">
                          {i + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </>
              )}

              {activeSnippet && (
                <>
                  <h4 className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{section.codeTitle}</h4>
                  {snippets.length > 1 && (
                    <div className="mb-2.5 inline-flex gap-1 rounded-lg bg-gray-100 p-1">
                      {snippets.map((snip, i) => (
                        <button
                          key={snip.id}
                          onClick={() => setDevSnip(i)}
                          className={`rounded-md px-3 py-1.5 text-xs font-medium ${
                            i === devSnip ? 'bg-white font-semibold text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}>
                          {snip.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <button
                      onClick={() => copySnippet(activeSnippet.code)}
                      className="absolute right-2.5 top-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-gray-500 hover:border-indigo-300 hover:text-indigo-600">
                      Copy
                    </button>
                    <pre className="overflow-x-auto whitespace-pre rounded-xl border border-gray-100 bg-gray-50 px-4 py-3.5 font-mono text-[11.5px] leading-relaxed text-gray-600">
                      {activeSnippet.code}
                    </pre>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmingDelete && (
        <OkCancelDialog onOk={handleDelete} onCancel={() => setConfirmingDelete(false)}>
          <p className="mb-6">
            Delete <strong>{brand.name || brand.key}</strong>? Envelopes already sent keep their appearance. New envelopes fall back to
            Verdocs defaults. This cannot be undone.
          </p>
        </OkCancelDialog>
      )}

      {working && (
        <div className="absolute inset-0 flex z-40 bg-[#ffffff7f] items-center justify-center">
          <Spinner mode="dark" />
        </div>
      )}
    </div>
  );
};
